import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { EmailLogEntry, page } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { useListState } from '../../lib/useList';
import { ErrorPanel } from '../../components/ErrorPanel';
import { Badge, EmptyRow, FilterBar, PageHeader, Pager, SelectField, Spinner, TableWrap, TextField, Th } from '../../components/ui';

const EmailPage = page(EmailLogEntry);

export function EmailsPage() {
  const list = useListState();
  const [status, setStatus] = useState('');
  const q = useQuery({
    queryKey: ['emails', { page: list.page, search: list.search, status }],
    queryFn: () => api.get('/emails', { query: { page: list.page, pageSize: 25, search: list.search, status }, schema: EmailPage }),
    placeholderData: keepPreviousData,
  });
  return (
    <>
      <PageHeader title={t('nav.emails')} description={t('nav.emails.desc')} />
      <FilterBar columns="sm:grid-cols-2">
        <TextField label={t('common.search')} type="search" value={list.search} onChange={(e) => list.setSearch(e.target.value)} />
        <SelectField
          label={t('common.status')}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            list.resetPage();
          }}
        >
          <option value="">{t('common.all')}</option>
          {(['queued', 'sending', 'sent', 'failed'] as const).map((s) => (
            <option key={s} value={s}>
              {t(`status.${s}`)}
            </option>
          ))}
        </SelectField>
      </FilterBar>
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <>
          <TableWrap caption={t('nav.emails')}>
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <Th>{t('emails.when')}</Th>
                <Th>{t('emails.to')}</Th>
                <Th>{t('emails.subject')}</Th>
                <Th>{t('common.status')}</Th>
                <Th>{t('emails.attempts')}</Th>
                <Th>{t('emails.error')}</Th>
              </tr>
            </thead>
            <tbody>
              {q.data.items.length === 0 ? <EmptyRow cols={6} /> : null}
              {q.data.items.map((m) => (
                <tr key={m.id} className="border-t border-slate-100 hover:bg-slate-50 align-top">
                  <td className="px-4 py-3">{formatDateTime(m.sentAt ?? m.createdAt)}</td>
                  <td className="px-4 py-3">{m.to}</td>
                  <td className="px-4 py-3">{m.subject}</td>
                  <td className="px-4 py-3">
                    <Badge tone={m.status === 'sent' ? 'good' : m.status === 'failed' ? 'bad' : 'warn'}>{t(`status.${m.status}`)}</Badge>
                  </td>
                  <td className="px-4 py-3">{m.attempts}</td>
                  <td className="px-4 py-3">{m.lastError ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
          <Pager page={q.data.page} pageSize={q.data.pageSize} total={q.data.total} onPage={list.setPage} />
        </>
      )}
    </>
  );
}
